<template>
  <div class="map" id="olMap"></div>
</template>

<script>
import 'ol/ol.css'
import { Map, View, Feature } from 'ol'
import TileLayer from 'ol/layer/Tile'
import OSM from 'ol/source/OSM'
import { defaults as defaultControls } from 'ol/control'
import { fromLonLat, transform } from 'ol/proj'
import { getDistance } from 'ol/sphere'
import Point from 'ol/geom/Point'
import Circle from 'ol/geom/Circle'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import Style from 'ol/style/Style'
import Icon from 'ol/style/Icon'
import Text from 'ol/style/Text'
import Fill from 'ol/style/Fill'
import Stroke from 'ol/style/Stroke'

export default {
  name: 'OsmMapView',
  props: {
      // 中心点坐标，格式为 [经度, 纬度]
      center: {
        type: Array,
        default: () => [0.0, 0.0]
        },
      // 缩放级别
      zoom: {
        type: Number,
        default: 14
      },
      // 动画持续时间（毫秒）
      duration: {
        type: Number,
        default: 1000
      },
      // 我的位置标记图片路径
      markerImageSrc: {
        type: String,
        default: '/image/kq_position_mine_blue.png'
      },
      // 企业位置标记图片路径
      companyMarkerImageSrc: {
        type: String,
        default: '/image/kq_position_company.png'
      },
    // 我的位置坐标
    myPosition: {
      type: Array,
      default: () => [0.0, 0.0]
    },
    // 画圆半径（米）
    circleRadius: {
      type: Number,
      default: 1000
    },
    // 是否显示我的位置
    showMyPosition: {
      type: Boolean,
      default: false
    },
    // 是否在考勤范围内
    inKqArea: {
      type: Boolean,
      default: false
    },
    // 条件显示相关props
    showCompanyMarker: {
      type: Boolean,
      default: true
    },
    showAttendanceText: {
      type: Boolean,
      default: true
    },

  },
  data() {
    return {
      map: null,
      markerLayer: null,
      circleLayer: null,
      mylocationLayer: null,
      isMobile: false
    }
  },
  mounted() {
    // 判断是否为移动设备
    this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    // 初始化地图
    this.initMap()
    // 监听窗口大小变化，调整地图大小
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    if (this.map) {
      this.map.dispose()
      window.removeEventListener('resize', this.handleResize)
    }
  },
  watch: {
    // 监听中心点变化，更新地图位置
    center: {
      handler(newCenter) {
        if (this.map && newCenter && newCenter.length === 2) {
          this.setCenter(newCenter, this.zoom)
          // 更新标记位置和圆形
          this.$nextTick(() => {
            this.updateMarkers();
            this.updateCircle();
          });
        }
      },
      deep: true
    },
    // 监听我的位置变化
    myPosition: {
      handler(newPosition) {
        if (this.map && newPosition && newPosition.length === 2) {
          this.$nextTick(() => {
            this.updateMarkers();
            // 如果显示我的位置，更新地图中心点
            if (this.showMyPosition) {
              this.setCenter(newPosition, null);
            }
          });
        }
      },
      deep: true
    },
    // 监听缩放级别变化，更新地图缩放
    zoom(newZoom) {
      if (this.map) {
        this.setZoom(newZoom)
      }
    },
    // 监听标记图片变化
    markerImageSrc() {
      if (this.map) {
        this.$nextTick(() => {
          this.updateMarkers();
        });
      }
    },
    // 监听考勤范围状态变化
    inKqArea() {
      if (this.map) {
        this.$nextTick(() => {
          this.updateMarkers();
        });
      }
    },
    // 监听showMyPosition变化
    showMyPosition() {
      if (this.map) {
        this.$nextTick(() => {
          this.updateMarkers();
          // 根据showMyPosition决定地图中心点
          const newCenter = this.showMyPosition ? this.myPosition : this.center;
          this.setCenter(newCenter, null);
        });
      }
    },
    // 监听条件显示相关props变化
    showCompanyMarker() {
      if (this.map) {
        this.$nextTick(() => {
          this.updateMarkers();
        });
      }
    },
    showAttendanceText() {
      if (this.map) {
        this.$nextTick(() => {
          this.updateMarkers();
        });
      }
    },

  },
  methods: {
    initMap() {
      // 直接使用props中的配置
      const mapCenter = this.center;
      const zoom = this.isMobile ? 16 : this.zoom;
      const myPosition = this.myPosition;
      
      // 根据showMyPosition决定地图中心点
      const mapViewCenter = this.showMyPosition ? myPosition : mapCenter;
      
      // 创建地图实例
      const map = new Map({
        target: 'olMap',
        view: new View({
          center: fromLonLat(mapViewCenter), // OSM使用EPSG:3857坐标系，需要转换
          zoom: zoom,
          projection: 'EPSG:3857'
        }),
        controls: defaultControls({
          zoom: false,
          attribution: false,
          rotate: false
        })
      })
      this.map = map

      // 为移动设备优化的额外设置
      if (this.isMobile) {
        map.getInteractions().forEach(interaction => {
          if (interaction && interaction.constructor && interaction.constructor.name === 'DoubleClickZoom') {
            map.removeInteraction(interaction);
          }
        });
      }

      // 添加OSM底图，使用完全开源免费的瓦片源
      const baseLayer = new TileLayer({
        preload: 2, // 预加载周围2个层级的瓦片
        source: new OSM({
          url: 'https://{a-c}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png',
          // 使用国内可快速访问的开源瓦片源
          urls: [
            'https://{a-c}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png',
            'https://tile.openstreetmap.bzh/br/{z}/{x}/{y}.png',
            'https://{a-c}.tile.openstreetmap.org/{z}/{x}/{y}.png'
          ],
          // 设置瓦片加载选项
          tileOptions: {
            crossOrigin: 'anonymous',
            transition: 0 // 禁用瓦片切换动画，提高拖动流畅度
          },
          // 增加缓存优化和更快的重试策略
          cacheSize: 200,
          retryDelay: function(times) {
            return Math.min(500 * Math.pow(2, times), 3000); // 更快的重试间隔
          },
          maxZoom: 19,
          tileLoadTimeout: 10000,
          // 优化用户体验的配置
          useExpiredTiles: true,
          useInterimTilesOnError: true,
          // 瓦片错误处理
          onTileError: function(error) {
            console.warn('瓦片加载失败，尝试备用源:', error);
          }
        })
      });
      
      // 添加底图图层到地图
      this.map.addLayer(baseLayer);
      
      // 当circleRadius > 0时，添加考勤范围圆形
      if (this.circleRadius > 0) {
        this.addCircle(mapCenter, this.circleRadius);
      }
      
      // 更新标记
      this.updateMarkers();
    },
    
    /**
     * 添加圆形
     */
    addCircle(center, radius) {
      if (this.circleLayer) {
        this.map.removeLayer(this.circleLayer);
      }
      
      // 确保center是有效的坐标数组
      if (!center || center.length !== 2) {
        return;
      }
      
      // 将经纬度坐标转换为EPSG:3857坐标系
      const center3857 = fromLonLat(center);
      
      // 创建圆形要素（在EPSG:3857坐标系中，半径直接使用米）
      const circleFeature = new Feature({
        geometry: new Circle(center3857, radius)
      });
      
      // 设置圆形样式
      const circleStyle = new Style({
        fill: new Fill({ color: 'rgba(16, 116, 244, 0.3)' }), // 内部填充色
        stroke: new Stroke({ 
          color: '#48B0F3', 
          width: 1,
          lineCap: 'round',
          lineJoin: 'round' 
        }) // 边框样式（添加圆角效果）
      });
      
      circleFeature.setStyle(circleStyle);
      
      // 创建圆形图层 (最低层级)
      this.circleLayer = new VectorLayer({
        source: new VectorSource({
          features: [circleFeature]
        }),
        zIndex: 1
      });
      
      this.map.addLayer(this.circleLayer);
    },
    
    /**
     * 更新圆形
     */
    updateCircle() {
      if (this.circleRadius > 0 && this.center && this.center.length === 2) {
        this.addCircle(this.center, this.circleRadius);
      }
    },
    
    /**
     * 更新标记
     */
    updateMarkers() {
      // 移除旧图层
      if (this.markerLayer) {
        this.map.removeLayer(this.markerLayer);
      }
      if (this.mylocationLayer) {
        this.map.removeLayer(this.mylocationLayer);
      }

      // 根据设备类型调整图标大小
      const iconScale = 0.65;
      
      // 显示企业标记，只要showCompanyMarker为true且有有效的center坐标
      if (this.showCompanyMarker && this.center && this.center.length === 2) {
        // 创建企业位置标记（需要转换坐标）
        const companyPoint = new Point(fromLonLat(this.center));
        const companyFeature = new Feature({ geometry: companyPoint });
        
        // 设置企业位置标记样式
        const companyIconStyle = new Style({
          image: new Icon({
            src: this.companyMarkerImageSrc,
            anchor: [0.5, 1],
            scale: 0.6
          })
        });
        
        // 只设置企业标记图标样式，不添加文字标注
        companyFeature.setStyle([companyIconStyle]);
        
        // 创建企业标记图层
        this.markerLayer = new VectorLayer({
          source: new VectorSource({
            features: [companyFeature]
          }),
          zIndex: 2
        });
        
        this.map.addLayer(this.markerLayer);
      }
      
      // 如果显示我的位置且坐标有效
      if (this.showMyPosition && this.myPosition && this.myPosition.length === 2) {
        // 创建我的位置标记（需要转换坐标）
        const myPoint = new Point(fromLonLat(this.myPosition));
        const myFeature = new Feature({ geometry: myPoint });
        
        // 设置我的位置标记样式
        const myIconStyle = new Style({
          image: new Icon({
            src: this.markerImageSrc,
            anchor: [0.5, 1],
            scale: iconScale
          })
        });
        
        // 条件：只要在考勤范围内，就显示文字标记
        if (this.inKqArea) {
          // 添加考勤范围文字标注 - 圆角蓝色背景，白色文字
          const attendanceTextStyle = new Style({
            text: new Text({
              text: '已进入考勤范围',
              font: '13px sans-serif',
              fill: new Fill({ color: '#ffffff' }), // 文字颜色为白色
              padding: [5, 10, 5, 10], // 内边距
              backgroundFill: new Fill({ color: '#038FEE' }), // 蓝色背景
              backgroundStroke: new Stroke({
                color: '#038FEE',
                width: 1
              }),
              backgroundRadius: [18, 18, 18, 18], // 圆角
              offsetY: -60 //确保显示在图标上方
            })
          });
          myFeature.setStyle([myIconStyle, attendanceTextStyle]);
        } else {
          myFeature.setStyle(myIconStyle);
        }
        
        // 添加我的位置标记 (高于企业标记)
        this.mylocationLayer = new VectorLayer({
          source: new VectorSource({
            features: [myFeature]
          }),
          zIndex: 3
        });
        
        this.map.addLayer(this.mylocationLayer);
      }
    },
    
    // 检查坐标是否在考勤范围内
    isInAttendanceRange(coordinate) {
      if (!this.center || this.center.length !== 2 || this.circleRadius <= 0) {
        return false;
      }
      
      // 将坐标转换为EPSG:3857坐标系以获取准确的距离计算（单位：米）
      const center3857 = fromLonLat(this.center);
      const coordinate3857 = fromLonLat(coordinate);
      
      // 使用ol的distance函数计算两点之间的距离（单位：米）
      const distanceInMeters = getDistance(center3857, coordinate3857);
      
      // 判断是否在范围内
      return distanceInMeters <= this.circleRadius;
    },
    
    /**
     * 设置地图中心点
     * @param {Array} center 中心点坐标 [经度, 纬度]
     * @param {Number} zoom 可选的缩放级别
     * @param {Number} duration 可选的动画持续时间
     */
    setCenter(center, zoom = null, duration = this.duration) {
      if (!this.map || !center || center.length !== 2) return
      
      const view = this.map.getView()
      const options = { duration }
      const transformedCenter = fromLonLat(center); // 转换为EPSG:3857坐标系
      
      if (zoom !== null) {
        view.animate({
          center: transformedCenter,
          zoom,
          ...options
        })
      } else {
        view.animate({
          center: transformedCenter,
          ...options
        })
      }
    },
    
    /**
     * 设置地图缩放级别
     * @param {Number} zoom 缩放级别
     * @param {Number} duration 可选的动画持续时间
     */
    setZoom(zoom, duration = this.duration) {
      if (!this.map || typeof zoom !== 'number') return
      
      this.map.getView().animate({
        zoom,
        duration
      })
    },
    
    /**
     * 获取当前地图中心点
     * @returns {Array} 当前中心点坐标 [经度, 纬度]
     */
    getCurrentCenter() {
      if (!this.map) return this.center
      // 从EPSG:3857坐标系转换回经纬度
      const center3857 = this.map.getView().getCenter();
      return center3857 ? transform(center3857, 'EPSG:3857', 'EPSG:4326') : this.center;
    },
    
    /**
     * 获取当前地图缩放级别
     * @returns {Number} 当前缩放级别
     */
    getCurrentZoom() {
      if (!this.map) return this.zoom
      return this.map.getView().getZoom()
    },
    
    /**
     * 处理窗口大小变化
     */
    handleResize() {
      if (this.map) {
        this.map.updateSize()
      }
    }
  }
}
</script>

<style>
/* 全局样式重置，确保地图容器正确显示 */
.osm-map-page .map-container #olMap {
  width: 100% !important;
  height: 100% !important;
  min-height: 500px;
  position: relative;
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}

/* 确保OpenLayers相关元素正确显示 */
.ol-viewport {
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}

.ol-unselectable {
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  outline: none !important;
  box-shadow: none !important;
}

/* 确保地图容器本身也没有边框和边距 */
.map {
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}

/* 移除OpenLayers可能添加的默认控制元素 */
.ol-control {
  display: none !important;
}

/* 确保所有OpenLayers相关元素都没有边框和边距 */
[class^="ol-"], [class*=" ol-"] {
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}

/* 确保div#olMap的直接父元素也没有边距 */
#olMap {
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  position: absolute;
  top: 0;
  left: 0;
}
</style>